import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useFormContext } from 'react-hook-form';

import type { Engine, ExportResult } from '@rendley/sdk';
import { EventsEnum, StorageIndexedDB } from '@rendley/sdk';
import { RendleyVideoEditor } from '@rendley/video-editor-ui';

import { useTheme } from '@/features/theme-switch';
import { config } from '@/shared/lib';
import { toastError } from '@/shared/lib/toastManager';

import { VideoContentIn } from '../lib/schema';

import { StorageS3WithPresignedURLs } from './StorageS3WithPresignedURLs';

export type VideoEditorRef = {
  serialize: () => ReturnType<Engine['serialize']> | undefined;
  export: () => Promise<ExportResult | null>;
  getEngine: () => Engine | null;
  getCanvas: () => HTMLCanvasElement | null;
  // syncAllMedia: () => Promise<void>;
  /** Dev mode: Get first video media blob from library without rendering */
  getFirstMediaBlob: () => Promise<Blob | null>;
};

type Props = {
  onClipChange: () => void;
  onLibraryChange: () => void;
};

const VideoEditor = forwardRef<VideoEditorRef, Props>((props, ref) => {
  const { darkTheme } = useTheme();
  const { getValues } = useFormContext<VideoContentIn>();

  const videoEditorRef = useRef<(RendleyVideoEditor & HTMLElement) | null>(
    null
  );
  const engineRef = useRef<Engine | null>(null);

  const serializedProject = getValues('editor_config');
  const project = useMemo(() => {
    if (serializedProject) {
      return JSON.parse(serializedProject);
    }

    return {};
  }, [serializedProject]);

  // @ts-ignore
  const { projectId } = project;

  const onRendleyReady = async () => {
    const Engine = await videoEditorRef.current?.getEngine();
    if (!Engine) return;

    const engine = Engine.getInstance();
    engineRef.current = engine;

    engine.getDisplay().setResolution(1280, 720);
    // TODO: it works, but we need to show that it is preview:
    // engine.getTimeline().setFps(1);

    Object.values(EventsEnum).forEach((eventKey) => {
      if (!eventKey.startsWith('clip:')) return;

      engine.events.addListener(eventKey, () => {
        props.onClipChange();
      });
    });

    Object.values(EventsEnum).forEach((eventKey) => {
      if (!eventKey.startsWith('library:')) return;

      engine.events.addListener(eventKey, () => {
        props.onLibraryChange();
      });
    });

    engine.events.addListener(EventsEnum.LIBRARY_MEDIA_UPDATED, async () => {
      try {
        await engine.getLibrary().syncAllMedia();
      } catch (error) {
        console.error(error);
      }
    });

    engine.events.addListener(EventsEnum.LIBRARY_ADDED, async () => {
      try {
        await engine.getLibrary().syncAllMedia();
      } catch (error) {
        console.error(error);
      }
    });

    engine.events.addListener(EventsEnum.LIBRARY_REMOVED, async () => {
      try {
        await engine.getLibrary().syncAllMedia();
      } catch (error) {
        console.error(error);
      }
    });

    try {
      if (!projectId) return;

      await Engine.deserialize(project);

      const mediaStatuses = Object.values(
        await Engine.getInstance().getLibrary().media
      ).map((media) => media.status);

      if (mediaStatuses.some((status) => status === 'none')) {
        toastError('Some media could not be loaded.');
      } else if (
        mediaStatuses.length &&
        mediaStatuses.every((status) => status === 'none')
      ) {
        toastError('Error during media loading');
      }
    } catch (error) {
      console.error(
        `Error while restoring video editor project. Details: ${error}`
      );
    }
  };

  useEffect(() => {
    if (!videoEditorRef.current) return () => {};

    const videoEditor = videoEditorRef.current;
    videoEditor.addEventListener('onReady', onRendleyReady);

    if (videoEditor)
      videoEditor.storages = [
        new StorageS3WithPresignedURLs(),
        new StorageIndexedDB(),
      ];

    return () => {
      if (!videoEditor) return;

      videoEditor.removeEventListener('onReady', onRendleyReady);
      videoEditor.destroy?.();
      // TODO: it is also possible that we need to clear all event listeners
      // but this is not easy with this terrible API
    };
  }, [videoEditorRef.current]);

  useImperativeHandle(ref, () => ({
    getCanvas: () => {
      if (!videoEditorRef.current) return null;

      return videoEditorRef.current.shadowRoot!.querySelector('canvas') || null;
    },

    getEngine: () => {
      return engineRef.current;
    },

    export: async () => {
      if (!engineRef.current || !videoEditorRef.current) return null;
      const engine = engineRef.current;
      const ApplicationStore =
        await videoEditorRef.current.getApplicationStore();
      const RendleyStore = await videoEditorRef.current.getRendleyStore();

      if (!ApplicationStore || !RendleyStore) return null;

      try {
        ApplicationStore.setSelectedClipId(null);
        ApplicationStore.setIsRendering(true);

        if (RendleyStore.render.blobUrl != null) {
          URL.revokeObjectURL(RendleyStore.render.blobUrl);
        }

        // TODO: can we delete this?
        // RendleyStore.resetRender();
        // throw Error();

        const result = await engine.export();

        if (!result) {
          throw new Error('Export failed: no output from the editor.');
        }

        // TODO: can we delete this?
        // RendleyStore.updateRender({
        //   blobUrl: URL.createObjectURL(result.blob),
        //   extension: result.extension,
        // });

        ApplicationStore.setIsRendering(false);

        return result;
      } catch {
        // this will render closable error modal
        RendleyStore.updateRender({ error: 'Rendering failed.' });

        return null;
      }
    },

    serialize: () => {
      return engineRef.current?.serialize();
    },

    // syncAllMedia: async () => {
    //   if (!engineRef.current) return;

    //   try {
    //     await engineRef.current.getLibrary().syncAllMedia();
    //   } catch (error) {
    //     console.error(
    //       `Unable to store video project media objects. Details: ${error}`
    //     );
    //   }
    // },

    getFirstMediaBlob: async () => {
      if (!engineRef.current) return null;

      const library = engineRef.current.getLibrary();
      const mediaItems = Object.values(library.media);
      const videoMedia = mediaItems.find((m) => m.type === 'video');

      if (!videoMedia) return null;

      // Try to get data from raw data first
      if (videoMedia.data && videoMedia.data.length > 0) {
        return new Blob([new Uint8Array(videoMedia.data)], {
          type: videoMedia.mimeType || 'video/mp4',
        });
      }

      // Fall back to fetching from blobUrl
      if (videoMedia.blobUrl) {
        try {
          const response = await fetch(videoMedia.blobUrl);
          return await response.blob();
        } catch {
          return null;
        }
      }

      return null;
    },
  }));

  return (
    <div className="flex">
      {config.ENVIRONMENT === 'development' ? (
        <>
          {/* @ts-expect-error Types are being imported from a CDN */}
          <rendley-video-editor
            ref={videoEditorRef}
            theme={darkTheme ? 'dark' : 'light'}
            licensename={config.RENDLEY_LICENSE_NAME}
            // licensekey={config.RENDLEY_LICENSE_KEY}
            enableremotevalidation
            class="!h-[800px] w-full overflow-hidden rounded-xl"
          />
        </>
      ) : (
        <>
          {/* @ts-expect-error Types are being imported from a CDN */}
          <rendley-video-editor
            ref={videoEditorRef}
            theme={darkTheme ? 'dark' : 'light'}
            licensename={config.RENDLEY_LICENSE_NAME}
            enableremotevalidation
            class="!h-[800px] w-full overflow-hidden rounded-xl"
          />
        </>
      )}
    </div>
  );
});

export default VideoEditor;
